import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface ParticipantState {
  active: boolean;
  ready: boolean;
  gapDurationSeconds: number;
}

interface HomePageGapCoordinatorContextValue {
  isGapActive: boolean;
  gapCycle: number;
  registerParticipant: (participantId: string) => void;
  unregisterParticipant: (participantId: string) => void;
  setParticipantActive: (participantId: string, active: boolean) => void;
  reportCycleBoundary: (
    participantId: string,
    gapDurationSeconds: number,
  ) => void;
}

const noop = () => {};

const defaultContextValue: HomePageGapCoordinatorContextValue = {
  isGapActive: false,
  gapCycle: 0,
  registerParticipant: noop,
  unregisterParticipant: noop,
  setParticipantActive: noop,
  reportCycleBoundary: noop,
};

const HomePageGapCoordinatorContext =
  createContext<HomePageGapCoordinatorContextValue>(defaultContextValue);

const clampGapDurationSeconds = (value: number | undefined): number => {
  const safeValue = Number.isFinite(value) ? Number(value) : 20;
  return Math.min(Math.max(safeValue, 1), 300);
};

export const HomePageGapCoordinatorProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isGapActive, setIsGapActive] = useState(false);
  const [gapCycle, setGapCycle] = useState(0);
  const participantsRef = useRef<Map<string, ParticipantState>>(new Map());
  const gapTimerRef = useRef<number | null>(null);

  const clearGapTimer = useCallback(() => {
    if (gapTimerRef.current !== null) {
      window.clearTimeout(gapTimerRef.current);
      gapTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearGapTimer();
    };
  }, [clearGapTimer]);

  const beginSharedGap = useCallback(
    (gapDurationSeconds: number) => {
      clearGapTimer();

      participantsRef.current.forEach((state) => {
        if (state.active) {
          state.ready = false;
        }
      });

      setIsGapActive(true);
      gapTimerRef.current = window.setTimeout(
        () => {
          setIsGapActive(false);
          setGapCycle((prev) => prev + 1);
        },
        clampGapDurationSeconds(gapDurationSeconds) * 1000,
      );
    },
    [clearGapTimer],
  );

  const evaluateBarrier = useCallback(() => {
    if (isGapActive) {
      return;
    }

    const activeParticipants = Array.from(
      participantsRef.current.values(),
    ).filter((state) => state.active);

    if (activeParticipants.length === 0) {
      return;
    }

    const allReady = activeParticipants.every((state) => state.ready);
    if (!allReady) {
      return;
    }

    const maxGapDurationSeconds = activeParticipants.reduce(
      (maxDuration, state) => Math.max(maxDuration, state.gapDurationSeconds),
      1,
    );

    beginSharedGap(maxGapDurationSeconds);
  }, [beginSharedGap, isGapActive]);

  const registerParticipant = useCallback((participantId: string) => {
    if (!participantsRef.current.has(participantId)) {
      participantsRef.current.set(participantId, {
        active: false,
        ready: false,
        gapDurationSeconds: 20,
      });
    }
  }, []);

  const unregisterParticipant = useCallback(
    (participantId: string) => {
      participantsRef.current.delete(participantId);
      evaluateBarrier();
    },
    [evaluateBarrier],
  );

  const setParticipantActive = useCallback(
    (participantId: string, active: boolean) => {
      const existing = participantsRef.current.get(participantId);
      const nextState: ParticipantState = existing
        ? { ...existing }
        : {
            active: false,
            ready: false,
            gapDurationSeconds: 20,
          };

      nextState.active = active;
      if (!active) {
        nextState.ready = false;
      }

      participantsRef.current.set(participantId, nextState);
      evaluateBarrier();
    },
    [evaluateBarrier],
  );

  const reportCycleBoundary = useCallback(
    (participantId: string, gapDurationSeconds: number) => {
      if (isGapActive) {
        return;
      }

      const existing = participantsRef.current.get(participantId);
      const nextState: ParticipantState = existing
        ? { ...existing }
        : {
            active: true,
            ready: false,
            gapDurationSeconds: 20,
          };

      if (!nextState.active) {
        return;
      }

      nextState.ready = true;
      nextState.gapDurationSeconds =
        clampGapDurationSeconds(gapDurationSeconds);
      participantsRef.current.set(participantId, nextState);
      evaluateBarrier();
    },
    [evaluateBarrier, isGapActive],
  );

  const contextValue = useMemo(
    () => ({
      isGapActive,
      gapCycle,
      registerParticipant,
      unregisterParticipant,
      setParticipantActive,
      reportCycleBoundary,
    }),
    [
      isGapActive,
      gapCycle,
      registerParticipant,
      unregisterParticipant,
      setParticipantActive,
      reportCycleBoundary,
    ],
  );

  return (
    <HomePageGapCoordinatorContext.Provider value={contextValue}>
      {children}
    </HomePageGapCoordinatorContext.Provider>
  );
};

export const useHomePageGapCoordinator =
  (): HomePageGapCoordinatorContextValue =>
    useContext(HomePageGapCoordinatorContext);
